import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import MobileHeader from '@/components/base/MobileHeader';
import ReviewItem from '@/pages/detail/components/ReviewItem';
import {
  getPartnerShopReviews,
  type PartnerShopReviewItem,
} from '@/api/partnerShopDetail';
import type { VendorReview } from '@/pages/detail/types';

interface FullReviewListProps {
  shopSlug: string;
  shopName: string;
  reviews: VendorReview[];
  onClose: () => void;
}

function FilterPillRow({
  categories,
  activeCategory,
  onSelect,
}: {
  categories: { label: string; count: number }[];
  activeCategory: string;
  onSelect: (label: string) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
      {categories.map((cat) => {
        const isActive = cat.label === activeCategory;
        return (
          <button
            key={cat.label}
            onClick={() => onSelect(cat.label)}
            className={`flex-shrink-0 px-3.5 h-[38px] rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
              isActive
                ? 'bg-[#B4563F] text-static-white'
                : 'bg-static-white text-[#5F5953] border border-[#E1DBD4] hover:border-[#C8705A]'
            }`}
          >
            {cat.label} {cat.count}
          </button>
        );
      })}
    </div>
  );
}

function normalizeReviewText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function mapReviewItem(review: PartnerShopReviewItem): VendorReview {
  const category = review.source || review.evaluationLevel || review.sentiment || review.tags?.[0] || '후기';

  return {
    id: review.id,
    title: review.title,
    source: review.source,
    author: review.writer?.nickname || '후기 작성자',
    writerId: review.writer?.id,
    writerNickname: review.writer?.nickname,
    writerProfileImageUrl: review.writer?.profileImageUrl,
    writerLevelName: review.writer?.memberLevelName,
    date: review.date,
    category,
    summary: normalizeReviewText(review.summary),
    content: normalizeReviewText(review.contentPreview || review.summary || review.title),
    reviewText: normalizeReviewText(review.reviewText || review.contentPreview || review.summary || review.title),
    tags: review.tags || [],
    goodPoints: review.goodPoints || [],
    checkPoints: review.checkPoints || [],
    originalUrl: review.originalUrl,
    evaluationLevel: review.evaluationLevel,
    sentiment: review.sentiment,
    confidence: review.confidence,
    photoUrls: review.photoThumbnails || [],
  };
}

export default function FullReviewList({ shopSlug, shopName, reviews, onClose }: FullReviewListProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const REVIEW_PAGE_SIZE = 50;
  const [reviewItems, setReviewItems] = useState<VendorReview[]>(reviews);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [visibleCount, setVisibleCount] = useState(REVIEW_PAGE_SIZE);

  useEffect(() => {
    let ignore = false;

    async function fetchReviews() {
      if (!shopSlug) {
        setErrorMessage('업체 주소가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const result = await getPartnerShopReviews(shopSlug, {
          preview: true,
        });

        if (!ignore) {
          setReviewItems((result.reviews || []).map(mapReviewItem));
          setActiveFilter('전체');
        }
      } catch (error) {
        console.error('[FullReviewList] failed to load partner shop reviews', error);

        if (!ignore) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : '후기 정보를 불러오지 못했습니다.'
          );
          setReviewItems(reviews);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    fetchReviews();

    return () => {
      ignore = true;
    };
  }, [reviews, shopSlug]);

  const filters = useMemo(() => {
    const categoryMap = new Map<string, number>();

    reviewItems.forEach((review) => {
      const category = review.category || review.evaluationLevel || review.sentiment || '후기';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    return [
      { label: '전체', count: reviewItems.length },
      ...Array.from(categoryMap.entries()).map(([label, count]) => ({ label, count })),
    ].filter((filter) => filter.count > 0);
  }, [reviewItems]);

  const [activeFilter, setActiveFilter] = useState('전체');

  const filteredReviews = useMemo(() => {
    if (activeFilter === '전체') return reviewItems;
    return reviewItems.filter((r) => r.category === activeFilter);
  }, [reviewItems, activeFilter]);

  const visibleReviews = useMemo(() => {
    return filteredReviews.slice(0, visibleCount);
  }, [filteredReviews, visibleCount]);

  const hasMoreReviews = visibleCount < filteredReviews.length;

  useEffect(() => {
    setVisibleCount(REVIEW_PAGE_SIZE);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [activeFilter, reviewItems]);

  const handleSelect = useCallback((label: string) => {
    setActiveFilter(label);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMoreReviews) return;

    const triggerPoint = el.scrollHeight * 0.7;
    if (el.scrollTop + el.clientHeight < triggerPoint) return;

    setVisibleCount((count) => Math.min(count + REVIEW_PAGE_SIZE, filteredReviews.length));
  }, [filteredReviews.length, hasMoreReviews]);

  return (
    <div
      ref={scrollContainerRef}
      className="fixed inset-0 z-50 bg-static-white overflow-y-auto"
      onScroll={handleScroll}
    >
      <div className="md:hidden">
        <MobileHeader
          onBack={onClose}
          title="전체 후기"
          rightContent={<>총 {reviewItems.length}개</>}
        >
          <p className="text-caption text-[#7A736C] mb-3 mt-3">
            실제 후기를 바탕으로 자주 언급된 내용을 모았어요.
          </p>
          <FilterPillRow
            categories={filters}
            activeCategory={activeFilter}
            onSelect={handleSelect}
          />
        </MobileHeader>
      </div>
      {/* ── Desktop Header ── */}
      <div className="hidden md:block">
        <div className="max-w-[900px] mx-auto px-8 pt-10 pb-6">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 text-sm text-[#5F5953] hover:text-[#B4563F] transition-colors cursor-pointer mb-6"
          >
            <i className="ri-arrow-left-line"></i> {shopName}으로 돌아가기
          </button>
          <h1 className="text-[28px] font-bold text-[#211C18] mb-2">전체 후기</h1>
          <p className="text-[15px] text-[#5F5953] mb-1">실제 후기를 바탕으로 자주 언급된 내용을 모았어요.</p>
          <p className="text-sm text-[#7A736C] mb-5">총 {reviewItems.length}개</p>
          <FilterPillRow
            categories={filters}
            activeCategory={activeFilter}
            onSelect={handleSelect}
          />
        </div>
      </div>
      <div className="px-5 pt-5 pb-24 md:px-8 md:max-w-[900px] mx-auto md:px-10 md:pt-0" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <div className="text-center py-16">
            <p className="text-[#7A736C] text-body2">후기를 불러오는 중입니다.</p>
          </div>
        ) : errorMessage && filteredReviews.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#7A736C] text-body2">{errorMessage}</p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#7A736C] text-body2">아직 해당 유형의 후기가 없어요.</p>
          </div>
        ) : (
          <>
            {visibleReviews.map((review) => (
              <ReviewItem key={review.id} review={review} />
            ))}
            {!hasMoreReviews && filteredReviews.length > 0 && (
              <div className="relative mt-8 mb-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-line-divider"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-static-white px-5 text-label2 text-label-assistive">
                    리스트의 마지막입니다
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
